<!--
  "All changes saved", and when a save takes more than a couple of seconds, a progress bar for
  it. Below that, a second bar while your AI is working (on something you asked, or in the
  background), with how far along it is when the job can tell.
-->
<script lang="ts">
  import { app } from "../lib/store.svelte.ts";
  import { aiQueue } from "../lib/ai/queue.svelte.ts";
  import { syncView } from "../lib/syncText.ts";
  import * as T from "../settings/sync.ts";
  import { fill, shows } from "../settings/index.ts";

  let now = $state(Date.now());
  $effect(() => {
    const t = setInterval(() => (now = Date.now()), T.SYNC_TICK_MS);
    return () => clearInterval(t);
  });

  const n = $derived(app.pendingCount);
  const retryIn = $derived(app.nextRetryAt ? Math.max(0, Math.ceil((app.nextRetryAt - now) / 1000)) : null);
  const view = $derived(
    syncView({ signedIn: app.signedIn, online: app.online, syncStatus: app.syncStatus, pending: n, retryIn }),
  );
  const showText = $derived(T.SHOW_SYNC_TEXT && shows(view.text));

  // ---- save progress: how many of this batch have gone through, shown after 2 seconds.
  let since = $state<number | null>(null);
  let batch = $state(0);
  $effect(() => {
    const saving = n > 0 && app.online && app.signedIn !== false;
    if (!saving) {
      since = null;
      batch = 0;
      return;
    }
    if (since === null) since = Date.now();
    if (n > batch) batch = n;
  });
  const showSave = $derived(since !== null && now - since > T.SYNC_BAR_AFTER_MS);
  const savePct = $derived(batch ? Math.round(((batch - n) / batch) * 100) : 0);

  // ---- AI work: a bar per running job that fills as it goes ("AI: Cards (4/16)"), then a
  // short "Next" list of what's queued (what you asked for first, then background work).
  const running = $derived.by(() => {
    const list: { id: string; label: string; fill: number; count: string }[] = [];
    for (const a of aiQueue.activities.filter((x) => x.state === "running")) {
      const p = a.total ? ([a.done ?? 0, a.total] as [number, number]) : (a.progress?.() ?? null);
      list.push({
        id: a.id,
        label: a.label,
        fill: p && p[1] ? p[0] / p[1] : (a.share ?? 0),
        count: p && p[1] ? ` (${p[0]}/${p[1]})` : "",
      });
    }
    const job = aiQueue.currentJob;
    if (job) {
      const p = job.progress?.() ?? null;
      list.push({ id: job.id, label: job.label, fill: p && p[1] ? p[0] / p[1] : 0, count: p && p[1] ? ` (${p[0]}/${p[1]})` : "" });
    }
    return list;
  });
  const next = $derived([
    ...aiQueue.activities.filter((x) => x.state === "queued").map((x) => x.label),
    ...aiQueue.waiting.map((j) => j.label),
  ]);
</script>

<div class="status">
  <button class="badge {view.tone}" onclick={() => app.syncNow()} title={T.SYNC_NOW_TITLE} aria-label={showText ? undefined : view.text || T.SYNC_NOW_TITLE} aria-live="polite">
    <span class="dot" aria-hidden="true"></span>
    {#if showText}{view.text}{/if}
  </button>
  {#if showSave}
    <div class="bar" role="progressbar" aria-label={T.SYNC_SAVING_BAR_LABEL} aria-valuemin={0} aria-valuemax={100} aria-valuenow={savePct}>
      <span style:width="{Math.max(6, savePct)}%"></span>
    </div>
  {/if}
  {#each running as j (j.id)}
    {@const pct = Math.round(j.fill * 100)}
    <div class="ai" aria-live="polite">
      <span class="label"><span>{fill(T.AI_JOB_TEXT, { label: j.label, count: j.count })}</span><span class="pct">{pct}%</span></span>
      <div class="bar" role="progressbar" aria-label={fill(T.AI_JOB_TEXT, { label: j.label, count: "" })} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <span style:width="{Math.max(3, pct)}%"></span>
      </div>
    </div>
  {/each}
  {#if next.length && T.SHOW_AI_NEXT && shows(T.AI_NEXT_TEXT)}
    <span class="next" title={next.join(", ")}>{fill(T.AI_NEXT_TEXT, { list: next.slice(0, T.AI_NEXT_SHOWN).join(", "), more: next.length > T.AI_NEXT_SHOWN ? ` +${next.length - T.AI_NEXT_SHOWN}` : "" })}</span>
  {/if}
</div>

<style>
  .pct {
    flex: none;
    font-variant-numeric: tabular-nums;
  }

  .next {
    overflow: hidden;
    max-width: 200px;
    color: var(--muted);
    font-size: var(--t-xs);
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .status {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    border: 0;
    background: none;
    color: var(--muted);
    font-size: var(--t-xs);
    font-weight: 500;
    cursor: pointer;
    text-align: left;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--ok);
    flex: none;
  }

  .busy .dot {
    background: var(--focus);
    animation: pulse 1.2s ease-in-out infinite;
  }

  .warn {
    color: var(--ink-2);
  }

  .warn .dot {
    background: #d9a514;
  }

  .ai {
    display: grid;
    gap: 3px;
  }

  .label {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    max-width: 200px;
    overflow: hidden;
    color: var(--muted);
    font-size: var(--t-xs);
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .bar {
    position: relative;
    height: 6px;
    max-width: 200px;
    border-radius: 999px;
    background: var(--rule);
    overflow: hidden;
  }

  .bar span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--focus);
    transition: width 0.3s ease;
  }

  .indeterminate span {
    position: absolute;
    width: 35%;
    animation: slide 1.3s ease-in-out infinite;
  }

  @keyframes slide {
    from {
      left: -35%;
    }
    to {
      left: 100%;
    }
  }

  @keyframes pulse {
    50% {
      opacity: 0.3;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .indeterminate span {
      left: 0;
      width: 100%;
      opacity: 0.5;
      animation: none;
    }
  }
</style>
